import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getAdminSocket } from '../../services/socketClient'
import { getFeedFreezeLog } from '../../services/systemTrackerService'
import CASINO_GAMES from '../../config/casinoGames'
import { SPORT_NAMES } from '../../utils/fixtures'
import './FeedMonitor.css'

/* Owner-only Feed Monitor: casino tables and in-play matches whose odds froze.
 *
 * "Stuck now" is pushed over the socket (`feedFreeze`, joined with
 * `subscribeFeedFreeze`) — no polling. The log is one REST call on open and on
 * filter / page change. The server keeps 24 h of rows; a feed still stuck after
 * that gets a fresh row with the same "stuck since".
 * Casino and Sports are separate tabs, each with its own log and filters.
 * Styling lives in FeedMonitor.css (the panel runs the BS4 Skote theme, so BS5
 * utility classes are not available).
 * Backend: server/systemTracker/feedFreeze.js, feedFreezeMonitor.js.
 * Plan: docs/plans/2026-10-22-feed-freeze-monitor.md
 */

const REASONS = {
  stamp_stale: { label: 'Feed not updating', tone: 'is-danger' },
  board_unchanged: { label: 'Board not changing', tone: 'is-warn' },
  no_feed: { label: 'No table updating', tone: 'is-danger' },
}
// Snapshot older than this = the monitor process is not running.
const MONITOR_DEAD_MS = 60_000
const MUTE_KEY = 'feedMonitorMuted'
const STATUS_FILTERS = [['', 'All'], ['open', 'Still stuck'], ['resolved', 'Recovered']]

// Per-kind wording and icon: casino rows are tables, sports rows are matches.
const SECTIONS = {
  casino: { title: 'Casino', noun: 'table', nameHead: 'Table', icon: 'bx-joystick' },
  sport: { title: 'Sports', noun: 'match', nameHead: 'Match', icon: 'bx-football' },
}

const fmtTime = (ts) => (ts
  ? new Date(ts).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', hour12: false, day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
  : '—')

const fmtDuration = (sec) => {
  if (sec == null || !Number.isFinite(sec)) return '—'
  const s = Math.max(0, Math.floor(sec))
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (d) return `${d}d ${h}h ${m}m`
  if (h) return `${h}h ${m}m`
  if (m) return `${m}m ${s % 60}s`
  return `${s % 60}s`
}

const feedName = (r) => {
  if (r.kind === 'casino') return r.feed_key === '*' ? 'Casino data cron stopped' : (CASINO_GAMES[r.feed_key]?.name || r.feed_key)
  return r.name || r.feed_key
}
// Detail under the name, so the owner can find the exact table / match.
const feedDetails = (r) => {
  if (r.kind === 'casino') {
    // Our own cron, not the vendor: every casino table is frozen for players.
    if (r.feed_key === '*') return ['all casino tables frozen, check pm2 casino-data-cron']
    return [r.feed_key, r.meta?.round && `round ${r.meta.round}`]
  }
  return [SPORT_NAMES[r.sport_id] || `sport ${r.sport_id ?? ''}`, r.meta?.cname, `gmid ${r.feed_key}`]
}
const stuckFor = (r, now) => (now - new Date(r.stuck_since).getTime()) / 1000

const readMuted = () => {
  try { return localStorage.getItem(MUTE_KEY) === '1' } catch { return false }
}

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)()
    const osc = ctx.createOscillator()
    osc.frequency.value = 880
    osc.connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + 0.25)
    osc.onended = () => ctx.close()
  } catch { /* audio blocked until the owner interacts with the page */ }
}

const Reason = ({ reason }) => {
  const r = REASONS[reason] || { label: reason, tone: 'is-dark' }
  return <span className={`fm-pill ${r.tone}`}>{r.label}</span>
}

const NameCell = ({ r }) => (
  <td>
    <div className="fm-name">{feedName(r)}</div>
    <div className="fm-sub">
      {feedDetails(r).filter(Boolean).map((d) => <span key={d}>{d}</span>)}
      {r.meta?.ended && <span>match ended</span>}
    </div>
  </td>
)

// Same column widths in both tables so they line up under each other.
const Cols = () => (
  <colgroup>
    <col />
    <col className="c-reason" />
    <col className="c-time" />
    <col className="c-time" />
    <col className="c-dur" />
  </colgroup>
)

/* One kind (casino / sport): "stuck now" on top, then its own history with its
   own filters and paging. `active` is this kind's slice of the socket list;
   `resolved` is the latest recovered batch pushed over the socket. */
function FeedSection({ kind, active, resolved, now, onDenied }) {
  const cfg = SECTIONS[kind]
  const [log, setLog] = useState({ rows: [], total: 0, page: 1, limit: 50 })
  const [filters, setFilters] = useState({ status: '', q: '' })
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const loadLog = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getFeedFreezeLog({ ...filters, kind, page, limit: 50 })
      if (res?.success) setLog(res)
    } catch (e) {
      if (e?.response?.status === 403) onDenied(e.response.data?.error || 'Owner access only')
    } finally {
      setLoading(false)
    }
  }, [filters, kind, page, onDenied])

  useEffect(() => { loadLog() }, [loadLog])

  // Recovered rows update in place on the visible page — no refetch.
  useEffect(() => {
    const mine = (resolved?.rows || []).filter((r) => r.kind === kind)
    if (!mine.length) return
    const byId = new Map(mine.map((r) => [r.id, r]))
    setLog((l) => ({ ...l, rows: l.rows.map((r) => byId.get(r.id) || r) }))
  }, [resolved, kind])

  const setFilter = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })) }
  const pages = Math.max(1, Math.ceil((log.total || 0) / (log.limit || 50)))

  return (
    <>
      <div className="fm-head">
        <h5>Stuck now</h5>
      </div>
      {active.length === 0 ? (
        <div className="fm-empty">
          <i className="bx bx-check-circle"></i>
          No {cfg.noun} is stuck. Every {cfg.title.toLowerCase()} feed is updating.
        </div>
      ) : (
        <>
          <div className="fm-table-wrap">
            <table className="fm-table">
              <Cols />
              <thead>
                <tr>
                  <th>{cfg.nameHead}</th>
                  <th>Reason</th>
                  <th>Stuck since</th>
                  <th>Noticed at</th>
                  <th className="t-right">Stuck for</th>
                </tr>
              </thead>
              <tbody>
                {active.map((r) => (
                  <tr key={r.id} className="fm-live">
                    <NameCell r={r} />
                    <td><Reason reason={r.reason} /></td>
                    <td className="t-num">{fmtTime(r.stuck_since)}</td>
                    <td className="t-num t-muted">{fmtTime(r.detected_at)}</td>
                    <td className="t-right"><span className="fm-stuck-for">{fmtDuration(stuckFor(r, now))}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="fm-gap" />
        </>
      )}

      <div className="fm-head">
        <h5>History</h5>
        <span className="fm-head-note">{log.total || 0} in the last 24 hours</span>
        <div className="fm-tools">
          <div className="fm-seg" role="group" aria-label="Filter by status">
            {STATUS_FILTERS.map(([v, label]) => (
              <button
                key={v || 'all'}
                type="button"
                className={filters.status === v ? 'is-active' : ''}
                aria-pressed={filters.status === v}
                onClick={() => setFilter('status', v)}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="fm-search">
            <i className="bx bx-search"></i>
            <input
              type="search"
              placeholder={`Search ${cfg.noun}`}
              aria-label={`Search ${cfg.noun}`}
              value={filters.q}
              onChange={(e) => setFilter('q', e.target.value)}
            />
          </label>
          <button type="button" className="fm-btn fm-btn-icon" onClick={loadLog} disabled={loading} title="Reload history" aria-label="Reload history">
            <i className={`bx bx-revision${loading ? ' bx-spin' : ''}`}></i>
          </button>
        </div>
      </div>
      <div className="fm-table-wrap">
        <table className="fm-table">
          <Cols />
          <thead>
            <tr>
              <th>{cfg.nameHead}</th>
              <th>Reason</th>
              <th>Stuck since</th>
              <th>Recovered at</th>
              <th className="t-right">Duration</th>
            </tr>
          </thead>
          <tbody>
            {loading && log.rows.length === 0 ? (
              <tr><td colSpan={5} className="fm-none">Loading…</td></tr>
            ) : log.rows.length === 0 ? (
              <tr><td colSpan={5} className="fm-none">Nothing got stuck in the last 24 hours.</td></tr>
            ) : log.rows.map((r) => (
              <tr key={r.id}>
                <NameCell r={r} />
                <td><Reason reason={r.reason} /></td>
                <td className="t-num">
                  {fmtTime(r.stuck_since)}
                  <span className="fm-time-sub">noticed {fmtTime(r.detected_at)}</span>
                </td>
                <td className="t-num">
                  {r.resolved_at ? fmtTime(r.resolved_at) : <span className="fm-pill is-danger">Still stuck</span>}
                </td>
                <td className="t-right t-num">
                  {fmtDuration(r.resolved_at ? r.duration_sec : stuckFor(r, now))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="fm-pager">
          <button type="button" className="fm-btn fm-btn-icon" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
            <i className="bx bx-chevron-left"></i>
          </button>
          <span>Page {page} of {pages}</span>
          <button type="button" className="fm-btn fm-btn-icon" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
            <i className="bx bx-chevron-right"></i>
          </button>
        </div>
      )}
    </>
  )
}

export default function FeedMonitor() {
  const token = useSelector((s) => s.auth.token)
  const [tab, setTab] = useState('casino')
  const [active, setActive] = useState([])
  const [resolved, setResolved] = useState(null)
  const [snapAt, setSnapAt] = useState(null)
  const [denied, setDenied] = useState('')
  const [now, setNow] = useState(Date.now())
  const [muted, setMuted] = useState(readMuted)
  const prevCount = useRef(null)
  const mutedRef = useRef(muted)
  mutedRef.current = muted

  // Live list over the socket.
  useEffect(() => {
    if (!token) return undefined
    const socket = getAdminSocket(token)
    if (!socket) return undefined
    const onPush = (p) => {
      if (!p) return
      if (p.error) { setDenied(p.error); return }
      const list = Array.isArray(p.active) ? p.active : []
      setActive(list)
      setSnapAt(p.at || null)
      if (prevCount.current != null && list.length > prevCount.current && !mutedRef.current) beep()
      prevCount.current = list.length
      if (Array.isArray(p.resolved) && p.resolved.length) setResolved({ rows: p.resolved })
    }
    const subscribe = () => socket.emit('subscribeFeedFreeze', { token })
    socket.on('feedFreeze', onPush)
    // Rooms are lost on reconnect — re-join on every connect.
    socket.on('connect', subscribe)
    if (socket.connected) subscribe()
    return () => {
      socket.off('feedFreeze', onPush)
      socket.off('connect', subscribe)
      socket.emit('unsubscribeFeedFreeze')
    }
  }, [token])

  // Local clock for the "stuck for" column — no request.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  const toggleMute = () => {
    const next = !muted
    setMuted(next)
    try { localStorage.setItem(MUTE_KEY, next ? '1' : '0') } catch { /* private mode */ }
  }

  const monitorDead = !snapAt || now - new Date(snapAt).getTime() > MONITOR_DEAD_MS
  const lists = {
    casino: active.filter((r) => r.kind === 'casino'),
    sport: active.filter((r) => r.kind === 'sport'),
  }
  const countCell = (key) => {
    const n = lists[key].length
    return (
      <div className="fm-cell">
        <span className="fm-cell-label">{SECTIONS[key].title}</span>
        <div className={`fm-cell-value ${n ? 'is-danger' : 'is-ok'}`}>
          {n} <small>{n === 1 ? 'feed stuck' : 'feeds stuck'}</small>
        </div>
      </div>
    )
  }

  return (
    <div className="fm">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">Feed Monitor</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><Link to="/admin/home">Home</Link></li>
                <li className="breadcrumb-item"><Link to="/admin/system-tracker">System Tracker</Link></li>
                <li className="breadcrumb-item active"><span aria-current="location">Feed Monitor</span></li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {denied ? (
        <div className="fm-warn-bar"><i className="bx bx-error-circle"></i>{denied}</div>
      ) : (
        <>
          <div className="card fm-status">
            <div className="fm-cell">
              <span className="fm-cell-label">Monitor</span>
              <div className={`fm-cell-value ${monitorDead ? 'is-warn' : 'is-ok'}`}>
                <span className="fm-dot" />{monitorDead ? 'Not running' : 'Live'}
              </div>
              <span className="fm-cell-note">
                {snapAt ? `Last check ${fmtDuration((now - new Date(snapAt).getTime()) / 1000)} ago` : 'No check received yet'}
              </span>
            </div>
            {countCell('casino')}
            {countCell('sport')}
            <div className="fm-cell">
              <span className="fm-cell-label">Alert sound</span>
              <button type="button" className={`fm-btn${muted ? '' : ' is-on'}`} onClick={toggleMute} aria-pressed={!muted}>
                <i className={`bx ${muted ? 'bx-bell-off' : 'bx-bell'}`}></i>
                {muted ? 'Sound off' : 'Sound on'}
              </button>
              <span className="fm-cell-note">Beeps when a new feed gets stuck</span>
            </div>
          </div>

          {monitorDead && (
            <div className="fm-warn-bar">
              <i className="bx bx-error-circle"></i>
              The monitor has not checked in for over a minute, so this page may be out of date.
              Start it with pm2 (feed-freeze-monitor).
            </div>
          )}

          <div className="card">
            <div className="card-body">
              <ul className="fm-tabs" role="tablist">
                {Object.keys(SECTIONS).map((key) => (
                  <li key={key}>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === key}
                      className={`fm-tab${tab === key ? ' is-active' : ''}`}
                      onClick={() => setTab(key)}
                    >
                      <i className={`bx ${SECTIONS[key].icon}`}></i>
                      {SECTIONS[key].title}
                      {lists[key].length > 0 && <span className="fm-count">{lists[key].length}</span>}
                    </button>
                  </li>
                ))}
              </ul>
              {/* Both stay mounted so a tab switch keeps its filters, page and history. */}
              {Object.keys(SECTIONS).map((key) => (
                <div key={key} hidden={tab !== key}>
                  <FeedSection kind={key} active={lists[key]} resolved={resolved} now={now} onDenied={setDenied} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
